import { CSS } from '@dnd-kit/utilities';
import { UniqueIdentifier, useDraggable, useDroppable } from '@dnd-kit/core';

import { ClassTeam } from '../ClassesTag/ClassesTag';
import { TeamItem } from './TeamItem';
import { useCallback, useMemo } from 'react';

export enum CombineType {
  COMBINABLE = 'combinable',
  NOT_COMBINABLE = 'notCombinable',
}

export type DraggableTeamItemProps = {
  team: ClassTeam;
  index: number;
  push: (obj: any) => void;
  remove: (index: number) => void;
  classId: number | string;
  teams: ClassTeam[];
  setField: (field: string, value: any, shouldValidate?: boolean) => void;
  canCombineFn: (sourceId: UniqueIdentifier, targetId: UniqueIdentifier) => boolean;
}

export const DraggableTeamItem = ({ team, index, push, remove, classId, teams, setField,
  canCombineFn,
}: DraggableTeamItemProps) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef: setDragRef,
    transform,
    over,
  } = useDraggable({ id: team.id, data: team });

  const { setNodeRef: setDropRef, isOver, active } = useDroppable({ id: team.id, data: team });

  const canCombine = useMemo(() => {
    if (isDragging) {
      return canCombineFn(team.id, over?.id as UniqueIdentifier);
    }
    return canCombineFn(team.id, active?.id as UniqueIdentifier);
  }, [canCombineFn, isDragging, over, team.id, active]);

  const combineStatus = useMemo(() => {
    if (isOver) {
      return canCombine ? CombineType.COMBINABLE : CombineType.NOT_COMBINABLE;
    }
    return null;
  }, [canCombine, isOver]);

  const setRefs = useCallback((node: HTMLElement | null) => {
    setDragRef(node);
    setDropRef(node);
  }, [setDragRef, setDropRef]);

  const style = {
    transform: canCombine && isOver ? 'none' : CSS.Translate.toString(transform),
    opacity: !isDragging ? 1 : 0.5,
  } as React.CSSProperties;


  return (
    <TeamItem
      team={team}
      index={index}
      push={push}
      remove={remove}
      classId={classId}
      teams={teams}
      setField={setField}
      ref={setRefs as unknown as (instance: HTMLDivElement | null) => void}
      combineStatus={combineStatus}
      // @ts-ignore - can't correctly type the prop for the component.
      draggableProps={{
        ...attributes,
        ...listeners,
      }}
      dragTargetProps={{
        style,
      }}
    />
  )
};
